import type { Medication } from '../../store/medicationsSlice'

interface MedicationDetailsProps {
  medication: Medication
}

function parseList(value: string): string[] {
  if (!value || !value.trim()) return []
  return value.split(',').map((s) => s.trim()).filter(Boolean)
}

export function MedicationDetails({ medication }: MedicationDetailsProps) {
  const contraindicated = parseList(medication.contraindicated_species)
  const interactions = parseList(medication.known_interactions)

  return (
    <div data-testid="medication-details-section" className="bg-surface border border-border rounded-[6px]">
      <div className="border-b border-border-light px-4 py-2.5">
        <h2 className="text-[14px] font-medium text-text-primary">Details</h2>
      </div>

      <div className="px-4 py-3 max-sm:px-3 flex flex-col gap-3">
        <div className="grid grid-cols-4 max-md:grid-cols-2 max-sm:grid-cols-1 gap-4">
          <div>
            <span className="text-[11px] text-text-muted block mb-0.5">Concentration</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-concentration">
              {medication.concentration || <span className="text-text-disabled italic">Not set</span>}
            </span>
          </div>
          <div>
            <span className="text-[11px] text-text-muted block mb-0.5">Default Dose Rate</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-dose-rate">
              {medication.default_dose_rate != null
                ? `${parseFloat(String(medication.default_dose_rate))} mg/kg`
                : <span className="text-text-disabled italic">Not set</span>}
            </span>
          </div>
          <div>
            <span className="text-[11px] text-text-muted block mb-0.5">Common Frequency</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-frequency">
              {medication.common_frequency || <span className="text-text-disabled italic">Not set</span>}
            </span>
          </div>
          <div>
            <span className="text-[11px] text-text-muted block mb-0.5">Route of Administration</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-route">
              {medication.form === 'Tablet' || medication.form === 'Capsule' || medication.form === 'Liquid' || medication.form === 'Chewable'
                ? 'Oral'
                : medication.form === 'Injectable'
                  ? 'Injection'
                  : medication.form === 'Topical'
                    ? 'Topical'
                    : medication.form}
            </span>
          </div>
        </div>

        <div>
          <span className="text-[11px] text-text-muted block mb-0.5">Contraindicated Species</span>
          <div data-testid="medication-contraindicated-species">
            {contraindicated.length > 0 ? (
              <div className="flex gap-1.5 flex-wrap">
                {contraindicated.map((species) => (
                  <span
                    key={species}
                    className="inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium bg-[#f0dede] text-status-error"
                  >
                    {species}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-[13px] text-text-disabled italic">None</span>
            )}
          </div>
        </div>

        <div>
          <span className="text-[11px] text-text-muted block mb-0.5">Known Drug Interactions</span>
          <div data-testid="medication-interactions">
            {interactions.length > 0 ? (
              <div className="flex gap-1.5 flex-wrap">
                {interactions.map((drug) => (
                  <span
                    key={drug}
                    className="inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium bg-[#f0e8d0] text-status-warning"
                  >
                    {drug}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-[13px] text-text-disabled italic">None</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
